import { useEffect } from 'react';

interface StructuredDataProps {
  type?: 'organization' | 'article' | 'service' | 'website';
  data?: Record<string, unknown>;
}

export function StructuredData({ type = 'organization', data }: StructuredDataProps) {
  useEffect(() => {
    let schema: Record<string, unknown> = {};

    switch (type) {
      case 'organization':
        schema = {
          '@context': 'https://schema.org',
          '@type': ['Organization', 'ProfessionalService'],
          name: '7Sigma Partners',
          url: 'https://7sigma.io',
          logo: 'https://7sigma.io/assets/logo.png',
          image: 'https://7sigma.io/assets/social-preview.png',
          email: 'discovery@7sigma.io',
          description:
            '7Sigma Partners is a fractional CTO and senior technical leadership practice in Orlando, Florida: architecture, hands-on engineering, and rescue of vibe coded apps for startups and enterprises nationwide.',
          foundingDate: '2021',
          founder: {
            '@type': 'Person',
            name: 'Robert Christian',
            jobTitle: 'Principal',
            url: 'https://7sigma.io/principal',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: '255 S Orange Avenue, Suite 104',
            addressLocality: 'Orlando',
            addressRegion: 'FL',
            postalCode: '32801',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 28.5383,
            longitude: -81.3792,
          },
          hasMap:
            'https://www.google.com/maps/search/?api=1&query=255+S+Orange+Avenue+Suite+104+Orlando+FL+32801',
          areaServed: [
            { '@type': 'AdministrativeArea', name: 'Central Florida' },
            { '@type': 'Country', name: 'United States' },
          ],
          alternateName: ['7Sigma', '7Sigma Partners LLC'],
          legalName: '7Sigma Partners LLC',
          sameAs: [
            'https://www.linkedin.com/company/7sigma-companies',
            'https://github.com/7sigmallc',
            'https://x.com/7SigmaCompanies',
            'https://news.7sigma.io',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'discovery@7sigma.io',
            contactType: 'sales',
            areaServed: 'US',
            availableLanguage: ['English', 'Spanish'],
          },
          slogan: 'Leader · Builder · Operator',
          knowsAbout: [
            'Fractional CTO',
            'Technical Leadership',
            'AI Consulting',
            'AI Integration',
            'Custom Software Development',
            'Product Architecture',
            'Digital Transformation',
            'Blockchain Development',
            'Cloud Architecture',
          ],
        };
        break;

      case 'website':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '7Sigma Partners',
          alternateName: '7Sigma',
          url: 'https://7sigma.io',
        };
        break;

      case 'service':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Technology Consulting',
          provider: {
            '@type': 'Organization',
            name: '7Sigma',
          },
          areaServed: {
            '@type': 'AdministrativeArea',
            name: 'Central Florida',
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Technology Services',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Fractional CTO Services',
                  description: 'Technical leadership and strategy for growing companies',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'AI Consulting and Integration',
                  description: 'AI strategy, LLM integration, and custom AI solutions',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Custom Software Development',
                  description: 'MVP to production builds and product architecture',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Digital Transformation',
                  description: 'System modernization and measurable change',
                },
              },
            ],
          },
        };
        break;

      case 'article':
        schema = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          publisher: {
            '@type': 'Organization',
            name: '7Sigma',
            logo: {
              '@type': 'ImageObject',
              url: 'https://7sigma.io/assets/logo.png',
            },
          },
          ...data,
        };
        break;
    }

    // Merge with custom data if provided
    if (data && type !== 'article') {
      schema = { ...schema, ...data };
    }

    // Create or update the JSON-LD script
    let scriptElement = document.querySelector('script[type="application/ld+json"]');
    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptElement);
    }
    scriptElement.textContent = JSON.stringify(schema, null, 2);

    return () => {
      // Cleanup on unmount
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [type, data]);

  return null;
}
